/**
 * Блок «Как это работает»: три шага от пустого листа до готовых чертежей.
 * У каждого шага — маленький чертёж, который дорисовывается, когда блок заехал в экран.
 */
import "./landing.css";
import { UIKit } from "@/shared/ui";

const at = (seconds: number) => ({ "--d": `${seconds}s` }) as React.CSSProperties;

/** Шаг 1: стена тянется за курсором, рядом — её длина. */
const DrawWallsArt = () => (
	<svg viewBox="0 0 240 150" aria-hidden="true" className="size-full">
		<path className="lp-draw lp-ink" d="M40 120V30H150" pathLength={1} style={at(0.2)} />
		<path className="lp-draw lp-ink-accent" d="M150 30H200V120" pathLength={1} style={at(1)} />
		<path
			className="lp-draw lp-ink-thin"
			d="M150 18H200M150 14v8M200 14v8"
			pathLength={1}
			style={at(1.6)}
		/>
		<text className="lp-fade lp-svg-text" x="175" y="12" style={at(1.8)}>
			2 500
		</text>
		<g className="lp-pop" style={at(1.7)}>
			<path d="M200 120l9 3-4 2-2 4z" fill="#1f2733" />
		</g>
	</svg>
);

/** Шаг 2: дверь, розетки и светильник с кабелем. */
const PlaceObjectsArt = () => (
	<svg viewBox="0 0 240 150" aria-hidden="true" className="size-full">
		<path className="lp-ink" d="M40 20H200V130H120M90 130H40V20" />
		<path
			className="lp-draw lp-ink-thin"
			d="M90 130V100A30 30 0 0 1 120 130"
			pathLength={1}
			style={at(0.2)}
		/>
		<g className="lp-pop" style={at(0.5)} fill="#879b83">
			<path d="M40 60A6 6 0 0 1 40 72Z" />
			<path d="M200 90A6 6 0 0 0 200 102Z" />
		</g>
		<g className="lp-pop" style={at(0.7)}>
			<circle cx="120" cy="68" r="8" className="lp-ink-accent" />
			<path d="M114 62l12 12M126 62l-12 12" className="lp-ink-accent" />
		</g>
		<path
			className="lp-draw lp-ink-accent"
			d="M46 66H90V68H112M128 68H170V96H194"
			pathLength={1}
			strokeDasharray="1"
			style={at(0.9)}
		/>
	</svg>
);

/** Шаг 3: лист PDF с планом и штампом. */
const SheetArt = () => (
	<svg viewBox="0 0 240 150" aria-hidden="true" className="size-full">
		<rect x="50" y="10" width="140" height="130" className="lp-ink-thin" fill="#f7f5f0" />
		<path
			className="lp-draw lp-ink"
			d="M66 26H174V96H66ZM120 26V70M66 70H174"
			pathLength={1}
			style={at(0.2)}
		/>
		<path
			className="lp-draw lp-ink-thin"
			d="M66 104H174M100 104V132M140 104V132M66 132H174"
			pathLength={1}
			style={at(0.9)}
		/>
		<g className="lp-fade" style={at(1.3)}>
			<rect x="104" y="110" width="30" height="4" fill="#d7d5cc" />
			<rect x="104" y="120" width="22" height="4" fill="#d7d5cc" />
			<rect x="144" y="110" width="24" height="16" fill="#879b83" opacity="0.15" />
		</g>
	</svg>
);

const STEPS = [
	{
		title: "Начертите стены",
		text: "Кликами по сетке или точным вводом длины. Комнаты и площади распознаются сами, стены не пересекаются.",
		Art: DrawWallsArt,
	},
	{
		title: "Расставьте всё нужное",
		text: "Двери, окна, розетки, свет, трубы и мебель из каталога. Кабели и трубы идут под прямыми углами, как в жизни.",
		Art: PlaceObjectsArt,
	},
	{
		title: "Заберите чертежи",
		text: "План с размерами, развёртка каждой стены, 3D-вид и PDF для бригады. Всё обновляется вместе с планом.",
		Art: SheetArt,
	},
];

export const StepsSection = () => (
	<section id="how" aria-labelledby="lp-how-title" className="lp-grid-light scroll-mt-4">
		<div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
			<div data-reveal className="max-w-180">
				<UIKit.Text as="span" className="lp-rise lp-mono block text-xs text-muted">
					01 - 03 · Как это работает
				</UIKit.Text>
				<h2
					id="lp-how-title"
					className="lp-rise lp-display mt-4 text-landing-section-heading"
					style={at(0.08)}
				>
					Три шага до{" "}
					<UIKit.Text as="span" className="lp-outline">
						рабочего
					</UIKit.Text>{" "}
					чертежа
				</h2>
			</div>

			<ol className="mt-12 grid gap-4 md:grid-cols-3">
				{STEPS.map(({ title, text, Art }, i) => (
					<li
						key={title}
						data-reveal
						className="lp-card flex flex-col rounded-2xl border border-border bg-surface p-6"
					>
						<div className="lp-rise flex items-baseline justify-between" style={at(i * 0.08)}>
							<UIKit.Text as="span" className="lp-display lp-outline text-5xl text-ink">
								{`0${i + 1}`}
							</UIKit.Text>
						</div>
						<div className="my-4 aspect-8/5 rounded-xl bg-bg">
							<Art />
						</div>
						<h3 className="text-xl font-semibold tracking-normal">{title}</h3>
						<UIKit.Text className="mt-2 text-sm leading-copy-relaxed text-muted">{text}</UIKit.Text>
					</li>
				))}
			</ol>
		</div>
	</section>
);
